import React, { useEffect, useRef, useState } from "react";
import "./App.css";

const IMAGE_BASE = `${process.env.PUBLIC_URL}/images`;

const NAVIGATION_LINKS = [
  { label: "Solutions", target: "solutions" },
  { label: "Food Trading", target: "food-trading" },
  { label: "Merchandise", target: "merchandise" },
  { label: "Partners", target: "partners" },
  { label: "Contact", target: "contact" }
];

const HERO_STATS = [
  { label: "Global logistics hubs", value: "12" },
  { label: "On-time delivery", value: "99%" },
  { label: "Dedicated specialists", value: "40+" }
];

const SERVICE_ICONS = {
  road: (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <path
        d="M6 41h12l6-34 6 34h12"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M24 9v6m0 6v6m0 6v6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  sea: (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <path
        d="M6 30h36l-4-12H10l-4 12Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <path
        d="M10 18V9h8l6 9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 36c2 0 3.5 2 6 2s4-2 6-2 3.5 2 6 2 4-2 6-2 4 2 6 2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  ),
  air: (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <path
        d="m6 26 12-3 8-14 4 4-6 12 14-2 6 7-17 3-5 9h-6l5-11-15-5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <path
        d="m20 23 4 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  rail: (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <rect
        x="10"
        y="8"
        width="28"
        height="20"
        rx="3"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      />
      <path
        d="M14 20h20M18 34l-6 6m18-6 6 6m-12-6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  )
};

const SERVICE_CARDS = [
  {
    title: "Road Transport",
    description: "Cross-border trucking programs engineered for consistency and full visibility.",
    highlights: ["Temperature-controlled fleet", "Live milestone tracking"],
    icon: SERVICE_ICONS.road
  },
  {
    title: "Sea Freight",
    description: "Flexible FCL and LCL sailings across major ports with integrated customs support.",
    highlights: ["Priority space agreements", "Insurance and documentation"],
    icon: SERVICE_ICONS.sea
  },
  {
    title: "Air Freight",
    description: "Expedited air solutions for time-sensitive cargo with white-glove handling.",
    highlights: ["Express clearance teams", "Door-to-door coordination"],
    icon: SERVICE_ICONS.air
  },
  {
    title: "Rail Transport",
    description: "Reliable rail corridors linking Asia and Europe for sustainable delivery timelines.",
    highlights: ["Bonded warehousing", "Digitized shipment tracking"],
    icon: SERVICE_ICONS.rail
  }
];

const FOOD_TRADING_CATEGORIES = [
  {
    title: "Fresh Produce",
    description:
      "Temperature-managed fruit and vegetable sourcing with calibrated cold-chain handling from farm to retail shelf.",
    highlights: ["Seasonal procurement programs", "Quality inspections at origin", "24/7 condition monitoring"]
  },
  {
    title: "Dry Goods & Commodities",
    description:
      "Grains, pulses, and pantry essentials shipped in bulk or curated assortments for wholesale and hospitality clients.",
    highlights: ["Flexible volume contracts", "Custom labeling and documentation", "Integrated customs management"]
  },
  {
    title: "Processed & Specialty Foods",
    description:
      "High-value delicacies, packaged goods, and functional nutrition products handled with compliant storage and swift last-mile delivery.",
    highlights: ["Multi-market regulatory support", "Supplier audits", "End-to-end traceability"]
  }
];

const FOOD_TRADING_ASSURANCE = [
  "Supplier network across China, Turkey, the USA, and the European Union",
  "Compliance-ready documentation for import and export procedures",
  "Risk-managed transport with insurance, temperature control, and live status updates"
];

const MERCHANDISE_COLLECTION = [
  {
    title: "Luxury Bags",
    tag: "Leather Goods",
    description:
      "Premium leather bags by world-renowned artisans, featuring unique designs and superior materials for business and casual occasions.",
    image: `${IMAGE_BASE}/luxury-bags.png`
  },
  {
    title: "Designer Shoes",
    tag: "Footwear",
    description:
      "Hand-crafted designer footwear pairing Italian leather with modern comfort, in classic and contemporary styles.",
    image: `${IMAGE_BASE}/designer-shoes.png`
  }
];

const PARTNER_LOGOS = [
  { name: "ZIM Integrated Shipping", src: `${IMAGE_BASE}/partner-zim.png` },
  { name: "CMA CGM", src: `${IMAGE_BASE}/partner-cma-cgm.png` },
  { name: "Mediterranean Shipping Company", src: `${IMAGE_BASE}/partner-msc.png` },
  { name: "Maersk", src: `${IMAGE_BASE}/partner-maersk.svg` },
  { name: "COSCO Shipping", src: `${IMAGE_BASE}/partner-logo-190x90.png` },
  { name: "Evergreen Line", src: `${IMAGE_BASE}/partner-logo-2.png` }
];

const CONTACT_DETAILS = [
  {
    title: "Direct Line",
    primary: "+380 96 795 0218",
    description: "Call or WhatsApp for real-time shipment updates.",
    href: "tel:+380967950218"
  },
  {
    title: "Global Office",
    primary: "Room 8, S-V, 6/F, Valiant Industrial Centre",
    description: "2-12 Au Pui Wan Street, Fo Tan, Hong Kong.",
    href: "https://maps.google.com/?q=VALIANT+INDUSTRIAL+CENTRE+Fo+Tan+Hong+Kong"
  },
  {
    title: "Operations Desk",
    primary: "info@broknav.com",
    description: "Send lane requests or product specs for a 1-business-day reply.",
    href: "mailto:info@broknav.com"
  }
];

const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

function useReveal() {
  useEffect(() => {
    const elements = document.querySelectorAll(".reveal");
    if (!("IntersectionObserver" in window) || elements.length === 0) {
      elements.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

function useHeaderScroll() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return scrolled;
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const formRef = useRef(null);
  const scrolled = useHeaderScroll();
  useReveal();

  const toggleMenu = () => setMenuOpen((prev) => !prev);

  const handleSubmit = (event) => {
    event.preventDefault();
    alert(`Message sent! Email: ${email}, Message: ${message}`);
    setEmail("");
    setMessage("");
  };

  // Duplicate logos for seamless marquee loop
  const marqueeLogos = [...PARTNER_LOGOS, ...PARTNER_LOGOS];

  return (
    <div className="app-shell">
      <header className={`site-header ${scrolled ? "is-scrolled" : ""} ${menuOpen ? "header-open" : ""}`}>
        <div className="brand" onClick={() => scrollToSection("home")}>
          <img className="brand-logo" src={`${IMAGE_BASE}/broknav-hero.png`} alt="Brokway Navigate emblem" />
          <div className="brand-text">
            <span className="brand-name">
              <span className="brand-mark">BROKWAY</span> NAVIGATE
            </span>
            <span className="brand-tag">Global Logistics · Hong Kong</span>
          </div>
        </div>

        <button
          className={`nav-toggle ${menuOpen ? "is-active" : ""}`}
          onClick={toggleMenu}
          aria-label="Toggle navigation"
          aria-expanded={menuOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <nav className={`site-nav ${menuOpen ? "is-visible" : ""}`}>
          <ul>
            {NAVIGATION_LINKS.map((link) => (
              <li key={link.target}>
                <button
                  type="button"
                  onClick={() => {
                    scrollToSection(link.target);
                    setMenuOpen(false);
                  }}
                >
                  {link.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <button
          type="button"
          className="header-cta"
          onClick={() => scrollToSection("contact")}
        >
          Request Quote
        </button>
      </header>

      <main>
        <section id="home" className="hero">
          <div
            className="hero-bg"
            style={{ backgroundImage: `url(${IMAGE_BASE}/hero-maritime.jpg)` }}
            aria-hidden="true"
          />
          <div className="hero-inner">
            <div className="hero-content">
              <p className="eyebrow reveal">Global Logistics & Trading</p>
              <h1 className="reveal" data-delay="1">
                Premium logistics, <span className="accent">moving global commerce</span> with confidence.
              </h1>
              <p className="lead reveal" data-delay="2">
                Secure, reliable cargo delivery from China, Turkey, USA and Europe. Our network connects major ports with end-to-end logistics — engineered around your timelines.
              </p>
              <div className="hero-actions reveal" data-delay="3">
                <button className="btn btn-primary" onClick={() => scrollToSection("solutions")}>
                  Explore Solutions <span className="btn-arrow">→</span>
                </button>
                <button className="btn btn-secondary" onClick={() => scrollToSection("food-trading")}>
                  Food Trading Program
                </button>
              </div>
              <dl className="hero-stats reveal" data-delay="4">
                {HERO_STATS.map((item) => (
                  <div key={item.label}>
                    <dt>{item.value}</dt>
                    <dd>{item.label}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="hero-visual reveal" data-delay="2" aria-hidden="true">
              <div className="hero-visual-frame">
                <img src={`${IMAGE_BASE}/broknav-hero.png`} alt="Brokway Navigate logistics emblem" />
              </div>
            </div>
          </div>
        </section>

        <section id="solutions" className="section solutions">
          <div className="section-header reveal">
            <p className="eyebrow">Integrated Logistics</p>
            <h2>Turnkey delivery solutions, tailored to your trade.</h2>
            <p className="section-intro">
              We provide secure cargo delivery on a turnkey basis from China, Turkey, the USA, and Europe — with operations specialists assigned to every shipment.
            </p>
          </div>
          <div className="card-grid">
            {SERVICE_CARDS.map((card, idx) => (
              <article className="card reveal" data-delay={(idx % 4) + 1} key={card.title}>
                <div className="card-icon" aria-hidden="true">
                  {card.icon}
                </div>
                <h3>{card.title}</h3>
                <p>{card.description}</p>
                <ul className="card-highlights">
                  {card.highlights.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section id="food-trading" className="section food-trading">
          <div className="section-header reveal">
            <p className="eyebrow">Food Trading</p>
            <h2>End-to-end food product distribution.</h2>
            <p className="section-intro">
              We connect producers and buyers with responsive logistics, compliance-ready documentation, and precision temperature-controlled transport for food-grade products.
            </p>
          </div>
          <div className="food-grid">
            {FOOD_TRADING_CATEGORIES.map((category, idx) => (
              <article className="food-card reveal" data-delay={(idx % 4) + 1} key={category.title}>
                <h3>{category.title}</h3>
                <p>{category.description}</p>
                <ul>
                  {category.highlights.map((highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <aside className="assurance reveal">
            <h3>How we guarantee quality</h3>
            <ul>
              {FOOD_TRADING_ASSURANCE.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </aside>
        </section>

        <section id="merchandise" className="section merchandise">
          <div className="section-header reveal">
            <p className="eyebrow">Specialty Merchandise</p>
            <h2>Curated goods with dedicated handling.</h2>
            <p className="section-intro">
              From luxury fashion to precision timepieces, we manage secure sourcing, verification, and delivery for premium merchandise portfolios.
            </p>
          </div>
          <div className="merch-grid">
            {MERCHANDISE_COLLECTION.map((item, idx) => (
              <article className="merch-card reveal" data-delay={(idx % 4) + 1} key={item.title}>
                <img src={item.image} alt={item.title} loading="lazy" />
                <div className="merch-content">
                  <span className="merch-tag">{item.tag}</span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section id="partners" className="section partners">
          <div className="section-header is-centered reveal">
            <p className="eyebrow">Network</p>
            <h2>Trusted by strategic partners.</h2>
            <p className="section-intro">
              Our partner ecosystem strengthens every shipment with specialist customs brokers, warehouse operators, and technology providers.
            </p>
          </div>
          <div className="partner-marquee" aria-label="Partner logos">
            <div className="partner-track">
              {marqueeLogos.map((logo, idx) => (
                <div className="partner-logo" key={`${logo.name}-${idx}`}>
                  <img src={logo.src} alt={logo.name} loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="cta-banner">
          <div className="cta-inner reveal">
            <div className="cta-text">
              <h2>Ready to move your next shipment?</h2>
              <p>Share your route, cargo profile, or sourcing brief. Our specialists respond within one business day with a tailored plan and pricing.</p>
            </div>
            <div className="cta-actions">
              <button className="btn btn-primary" onClick={() => {
                scrollToSection("contact");
                setTimeout(() => formRef.current?.querySelector("input")?.focus(), 600);
              }}>
                Start a Conversation <span className="btn-arrow">→</span>
              </button>
              <a className="btn btn-secondary" href="mailto:info@broknav.com">
                info@broknav.com
              </a>
            </div>
          </div>
        </section>

        <section id="contact" className="section contact">
          <div className="section-header reveal">
            <p className="eyebrow">Contact</p>
            <h2>Let’s move your next shipment.</h2>
            <p className="section-intro">
              Share your project details and our logistics specialists will respond within one business day.
            </p>
          </div>
          <div className="contact-wrapper">
            <form className="contact-form reveal" onSubmit={handleSubmit} ref={formRef}>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />

              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                placeholder="How can we support your logistics or trading goals?"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                required
              ></textarea>

              <button type="submit" className="btn btn-primary">
                Send Message <span className="btn-arrow">→</span>
              </button>
            </form>
            <div className="contact-details reveal" data-delay="1">
              <h3>Talk to a logistics specialist</h3>
              <ul className="contact-list">
                {CONTACT_DETAILS.map((detail) => {
                  const externalLink = detail.href.startsWith("http");
                  return (
                    <li key={detail.title}>
                      <span>{detail.title}</span>
                      <a
                        href={detail.href}
                        {...(externalLink ? { target: "_blank", rel: "noreferrer" } : {})}
                      >
                        {detail.primary}
                      </a>
                      <p>{detail.description}</p>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-grid">
          <div className="footer-brand">
            <span className="brand-name">
              <span className="brand-mark">BROKWAY</span> NAVIGATE
            </span>
            <p>Premium logistics solutions connecting China, Turkey, the USA and Europe through a Hong Kong-based operations desk.</p>
          </div>
          <div className="footer-col">
            <h4>Solutions</h4>
            <ul>
              <li><button type="button" onClick={() => scrollToSection("solutions")}>Road Transport</button></li>
              <li><button type="button" onClick={() => scrollToSection("solutions")}>Sea Freight</button></li>
              <li><button type="button" onClick={() => scrollToSection("solutions")}>Air Freight</button></li>
              <li><button type="button" onClick={() => scrollToSection("solutions")}>Rail Transport</button></li>
            </ul>
          </div>
          <div className="footer-col">
            <h4>Trading</h4>
            <ul>
              <li><button type="button" onClick={() => scrollToSection("food-trading")}>Food Trading</button></li>
              <li><button type="button" onClick={() => scrollToSection("merchandise")}>Specialty Merchandise</button></li>
              <li><button type="button" onClick={() => scrollToSection("partners")}>Partners</button></li>
            </ul>
          </div>
          <div className="footer-col">
            <h4>Contact</h4>
            <ul>
              <li><a href="tel:+380967950218">+380 96 795 0218</a></li>
              <li><a href="mailto:info@broknav.com">info@broknav.com</a></li>
              <li>Room 8, S-V, 6/F, Valiant Industrial Centre, 2-12 Au Pui Wan Street, Fo Tan, Hong Kong</li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <span>© 2026 <strong>BROKWAY NAVIGATE LIMITED</strong> — All Rights Reserved</span>
          <span>Business Reg. No. <strong>78846197</strong></span>
        </div>
      </footer>
    </div>
  );
}

export default App;
